// searchHandler.js
import layerLibrary from "./LayerLibrary";
import ProjectManager from "./ProjectManager";
import InstanceLibrary from "../libraries/RectangleLibrary";
import labelLibrary from "../libraries/LabelLibrary";
import polygonLibrary from "../libraries/polygonLibrary";
import layoutEngine from "../libraries/layoutEngine";
import selectionLibrary from "../libraries/SelectionLibrary";

// Helper: resolve a layer object
function resolveLayer(layerObj, shape) {
  if (!layerObj && shape.layer !== undefined && shape.datatype !== undefined) {
    return layerLibrary.getLayerByNumber(shape.layer, shape.datatype);
  }
  return layerObj;
}

// Helper: filter shapes by property/value
function filterByProperty(shape, property, value) {
  let layerObj = layerLibrary.getLayerById(shape.layerId);
  layerObj = resolveLayer(layerObj, shape);

  switch (property) {
    case "layername":
      return (
        layerObj?.layer_name?.toLowerCase() === value.toLowerCase() ||
        shape.layerName?.toLowerCase() === value.toLowerCase() ||
        shape.layerId?.toLowerCase() === value.toLowerCase()
      );
    case "layerid":
      return shape.layerId === value || `${shape.layer}:${shape.datatype}` === value;
    case "width":
      return String(shape.width) === value;
    case "height":
      return String(shape.height) === value;
    case "instanceName":
      return shape.ref?.toLowerCase() === value.toLowerCase();
    
    // 🔹 NEW: filter by label text or name
    case "labelname":
    case "label":
      return (
        shape.text?.toLowerCase() === value.toLowerCase() ||
        shape.name?.toLowerCase() === value.toLowerCase()
      );
    
      // 🔹 Polygon: number of sides
    case "sides":
    case "numsides":
    case "numberofsides": {
      // Try to detect points array or vertices array
      const pts = shape.points || shape.vertices || shape.coords || [];
      if (!Array.isArray(pts)) return false;

      // Handle both nested arrays ([ [x, y], [x2, y2] ]) or flat coords ([x1, y1, x2, y2])
      const sides = Array.isArray(pts[0]) ? pts.length : pts.length / 2;
      return sides === Number(value)+1; // +1 to count correctly
    }

    default:
      return false;
  }
}

export function handleSearchRequest(e, context) {
  const { shapeType, property, value } = e.detail;
  const cellName = ProjectManager.getSelectedCell();
  let matches = [];

  // 🔹 RECTANGLES
  if (shapeType === "rectangle") {
    const createdRects = Object.values(InstanceLibrary.getAllInstances()[cellName] || {}).flat();
    const uploadedRects = layoutEngine
      .getUploadedCell(cellName)
      ?.elements?.filter(el => el.type === "rectangle") || [];

    matches = [...createdRects, ...uploadedRects].filter(r =>
      filterByProperty(r, property, value)
    );
  }

  // 🔹 LABELS
  if (shapeType === "label") {
    const createdLabels = labelLibrary.getAllLabels(cellName) || [];
    const uploadedLabels = layoutEngine
      .getUploadedCell(cellName)
      ?.elements?.filter(el => el.type === "label") || [];

    matches = [...createdLabels, ...uploadedLabels].filter(l =>
      filterByProperty(l, property, value)
    );
  }

  // 🔹 POLYGONS
  if (shapeType === "polygon") {
    const createdPolys = polygonLibrary.getAllPolygons(cellName) || [];
    const uploadedPolys = layoutEngine
      .getUploadedCell(cellName)
      ?.elements?.filter(el => el.type === "polygon") || [];

    matches = [...createdPolys, ...uploadedPolys].filter(p =>
      filterByProperty(p, property, value)
    );
  }

  // 🔹 INSTANCES
  if (shapeType === "instance") {
    const allCellsMap = layoutEngine.getAllCellsMap();
    const targetCell = allCellsMap[value];
    if (!targetCell) {
      console.warn(`❌ No cell found with name "${value}"`);
      return;
    }

    const createdInstances = (layoutEngine.getCell(cellName)?.instances || []).map(inst => ({
      ...inst,
      isUploaded: false
    }));

    const uploadedInstances = (layoutEngine.getUploadedCell(cellName)?.instances || []).map(inst => ({
      ...inst,
      isUploaded: true
    }));

    matches = [...createdInstances, ...uploadedInstances]
      .filter(inst => filterByProperty(inst, property, value))
      .map(inst => ({
        ...inst,
        type: "instance",
        name: inst.ref
      }));
  }

  if (!matches.length) {
    console.warn("❌ No matches found");
    return;
  }

  // ✅ Save selection
  context.selectedObjects = matches.map(obj => ({
    id: obj.id,
    type: shapeType,
    ref: obj,
    isUploaded: obj.isUploaded || false
  }));
  selectionLibrary.setSelection(context.selectedObjects);

  // ✅ Zoom to bounding box
  // const first = matches[0];
  // const canvasHeight = context.canvas.height;

  // if (shapeType === "rectangle") {
  //   context.smoothZoomToViolation({
  //     location: [
  //       [first.x, canvasHeight - first.y],
  //       [first.x + first.width, canvasHeight - first.y],
  //       [first.x + first.width, canvasHeight - (first.y + first.height)],
  //       [first.x, canvasHeight - (first.y + first.height)]
  //     ]
  //   });
  // } else if (shapeType === "polygon") {
  //   context.smoothZoomToViolation({
  //     location: first.points.map(([px, py]) => [px, canvasHeight - py])
  //   });
  // } else if (shapeType === "label") {
  //   const [lx, ly] = first.coordinates[0];
  //   const delta = 5;
  //   context.smoothZoomToViolation({
  //     location: [
  //       [lx - delta, canvasHeight - (ly - delta)],
  //       [lx + delta, canvasHeight - (ly - delta)],
  //       [lx + delta, canvasHeight - (ly + delta)],
  //       [lx - delta, canvasHeight - (ly + delta)]
  //     ]
  //   });
  // }

  const first = matches[0];
const canvasHeight = context.canvas.height;

// Helper to decide if shape was created manually
const isCreated = !first.source || first.source === "created"; 
// (If your created shapes don't have `source`, this condition will still detect them.)

// Function to fix Y based on type
const adjustY = (y) => (isCreated ? -y : canvasHeight - y);

if (shapeType === "rectangle") {
  const y1 = adjustY(first.y);
  const y2 = adjustY(first.y + first.height);

  context.smoothZoomToViolation({
    location: [
      [first.x, y1],
      [first.x + first.width, y1],
      [first.x + first.width, y2],
      [first.x, y2],
    ],
  });

} else if (shapeType === "polygon") {
  context.smoothZoomToViolation({
    location: first.points.map(([px, py]) => [px, adjustY(py)]),
  });

}
//  else if (shapeType === "label") {
//   const [lx, ly] = first.coordinates[0];
//   const delta = 5;
//   const adjY = adjustY(ly);

//   context.smoothZoomToViolation({
//     location: [
//       [lx - delta, adjY - delta],
//       [lx + delta, adjY - delta],
//       [lx + delta, adjY + delta],
//       [lx - delta, adjY + delta],
//     ],
//   });
// }


  context.draw();
}
